// The بیمار تمرینی form — its fields, its closed sets, and the two shapes the
// wire wants back.
//
// ── The fields that are NOT here, and never may be ────────────────────────
//
// There is no national id, no phone number, no birth date and no address on this
// form, on the `StudentCase` type, or in the store's proto. They are ABSENT
// rather than present-and-unused (core.md §3.1): a field that does not exist
// cannot be populated by a well-meaning later change, cannot be matched against,
// and cannot leak out of an export. `patients.national_id` carries a
// PLATFORM-WIDE unique index; nothing in this app may write there.
//
// ── Age is `ageYears` + `ageDisplay`, never a birth date ─────────────────
//
// `ageDisplay` exists because `ageYears = 0` silently turns every paediatric and
// neonatal case into a newborn of indeterminate age. «۶ ماهه» OVERRIDES the years.
//
// v2 adds blood type (closed set), weight and height. Pure and React-free.

import { SEXES, CASE_STATUSES, BLOOD_TYPES } from '../constants/vocabulary.js';

export { SEXES, BLOOD_TYPES };
export const STATUSES = CASE_STATUSES;
export const DEFAULT_STATUS = 'active';

/** Every field a string, because that is what an input holds. */
export const emptyCaseForm = () => ({
  title: '',
  displayName: '',
  caseRef: '',
  ageYears: '',
  ageDisplay: '',
  sex: '',
  bloodType: '',
  weightKg: '',
  heightCm: '',
  chiefComplaint: '',
  status: DEFAULT_STATUS,
  note: '',
});

const numText = (v) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) && n > 0 ? String(n) : '';
};

/**
 * A stored case → editable form values. `0` on a number means UNSTATED (the
 * BFF never sends null for an int; proto-loader turns an unset one into 0).
 */
export function caseToForm(row) {
  const src = row || {};
  return {
    title: src.title || '',
    displayName: src.displayName || '',
    caseRef: src.caseRef || '',
    ageYears: numText(src.ageYears),
    ageDisplay: src.ageDisplay || '',
    sex: SEXES.includes(src.sex) ? src.sex : '',
    bloodType: BLOOD_TYPES.includes(src.bloodType) ? src.bloodType : '',
    weightKg: numText(src.weightKg),
    heightCm: numText(src.heightCm),
    chiefComplaint: src.chiefComplaint || '',
    status: STATUSES.includes(src.status) ? src.status : DEFAULT_STATUS,
    note: src.note || '',
  };
}

/** A title is the one thing a patient cannot be filed without. */
export const titleProblem = (form) =>
  String(form?.title ?? '').trim() ? null : 'caseForm.validation.title';

const trimmed = (v) => String(v ?? '').trim();

/** Years, as an integer, or null when blank or unusable. Persian digits folded. */
export function parseAgeYears(value) {
  const raw = foldDigits(trimmed(value));
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

/** A decimal measurement, or null. */
export function parseMeasure(value) {
  const raw = foldDigits(trimmed(value)).replace(/[,٫]/g, '.'); // i18n-exempt: ٫ is the Arabic decimal separator, parsed not shown
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 10) / 10;
}

/** Persian and Arabic-Indic digits → ASCII, so a numeric field accepts a Persian keyboard. */
export function foldDigits(value) {
  return String(value ?? '')
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))) // i18n-exempt: digit folding
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d))); // i18n-exempt: digit folding
}

/**
 * Form → `CaseInput`, for `createCase`. Blank optional fields are OMITTED rather
 * than sent as `''` — an empty string stored where the student wrote nothing is
 * a value the prompt builder would go on to interpolate as a fact.
 */
export function toCreateInput(form) {
  const input = { title: trimmed(form?.title) };
  const optional = {
    displayName: trimmed(form?.displayName),
    caseRef: trimmed(form?.caseRef),
    ageDisplay: trimmed(form?.ageDisplay),
    sex: SEXES.includes(form?.sex) ? form.sex : '',
    bloodType: BLOOD_TYPES.includes(form?.bloodType) ? form.bloodType : '',
    chiefComplaint: trimmed(form?.chiefComplaint),
    note: trimmed(form?.note),
  };
  for (const [key, value] of Object.entries(optional)) if (value) input[key] = value;

  const ageYears = parseAgeYears(form?.ageYears);
  if (ageYears != null) input.ageYears = ageYears;
  const weightKg = parseMeasure(form?.weightKg);
  if (weightKg != null) input.weightKg = weightKg;
  const heightCm = parseMeasure(form?.heightCm);
  if (heightCm != null) input.heightCm = heightCm;

  if (STATUSES.includes(form?.status) && form.status !== DEFAULT_STATUS) input.status = form.status;
  return input;
}

/**
 * Form → `CaseUpdateInput`, for `updateCase`. ONLY WHAT CHANGED: an omitted field
 * is left alone; `''` (or `0` for a number) clears one. Returns `{}` when nothing
 * moved, and the caller skips the round trip on that.
 */
export function buildCaseUpdate(original, form) {
  const before = caseToForm(original);
  const patch = {};

  for (const key of ['title', 'displayName', 'caseRef', 'ageDisplay', 'chiefComplaint', 'note']) {
    const next = trimmed(form?.[key]);
    if (next !== before[key]) patch[key] = next;
  }

  const nextSex = SEXES.includes(form?.sex) ? form.sex : '';
  if (nextSex !== before.sex) patch.sex = nextSex;

  const nextBlood = BLOOD_TYPES.includes(form?.bloodType) ? form.bloodType : '';
  if (nextBlood !== before.bloodType) patch.bloodType = nextBlood;

  const nextStatus = STATUSES.includes(form?.status) ? form.status : before.status;
  if (nextStatus !== before.status) patch.status = nextStatus;

  const nextAge = parseAgeYears(form?.ageYears);
  if (nextAge !== parseAgeYears(before.ageYears)) patch.ageYears = nextAge == null ? 0 : nextAge;

  const nextWeight = parseMeasure(form?.weightKg);
  if (nextWeight !== parseMeasure(before.weightKg)) patch.weightKg = nextWeight == null ? 0 : nextWeight;

  const nextHeight = parseMeasure(form?.heightCm);
  if (nextHeight !== parseMeasure(before.heightCm)) patch.heightCm = nextHeight == null ? 0 : nextHeight;

  return patch;
}

/**
 * The age, as the patient should read it. `ageDisplay` OVERRIDES `ageYears`;
 * `0` means "said nothing". Returns '' when unstated.
 */
export function ageLabel(row, t) {
  const display = String(row?.ageDisplay ?? '').trim();
  if (display) return display;
  const years = Number(row?.ageYears ?? 0);
  if (!Number.isFinite(years) || years <= 0) return '';
  return t('common.units.years', { count: years });
}

/** One line describing the imaginary patient: name · sex · age. */
export function patientLine(row, t) {
  const parts = [];
  const name = String(row?.displayName ?? '').trim();
  parts.push(name || t('patients.unnamed'));
  if (SEXES.includes(row?.sex)) parts.push(t(`common.gender.${row.sex}`));
  const age = ageLabel(row, t);
  if (age) parts.push(age);
  return parts.join(' · ');
}

/** The name a header prints: the invented name, else the title. */
export const patientName = (row, t) =>
  String(row?.displayName ?? '').trim() || String(row?.title ?? '').trim() || t('patients.unnamed');

export default emptyCaseForm;
